/**
 * US Census adapter (M9-B) — ACS 5-Year demographic variables over TIGER/Line
 * boundaries, matching the Maptive teardown baseline (50+ variables across
 * population, age, income, race/ethnicity, housing, education, employment).
 *
 * Boundaries: TIGER/Line shapefiles (served as GeoJSON at ETL time) at four
 * tiers — state / county / zcta / tract. Values: the free Census Data API
 * (`api.census.gov`), whose response is a header row + rows of strings; the
 * boundary id is the concatenation of the geography columns (== the TIGER GEOID).
 *
 * Fetch is behind the {@link HttpClient} seam (fixtures in tests; a real
 * `fetch`-backed client + `CENSUS_API_KEY` at deploy time). Normalize is pure.
 */

import type { Boundary, DemographicDatum, DemographicVariable, Tier } from "../types";
import { buildCatalog, normalizeFeatureCollection, type FeatureCollection, type Seed } from "./shared";
import type { HttpClient, OverlayAdapter } from "./types";

export const US_CENSUS_SOURCE = "us_census" as const;
export const US_CENSUS_TIERS = ["state", "county", "zcta", "tract"] as const;

/** ACS 5-Year public-data attribution (feeds the M11 AttributionResolver). */
export const US_CENSUS_CITATION =
  "U.S. Census Bureau, American Community Survey (ACS) 5-Year Estimates. Public domain.";

/** The Census Data API host (deploy-time base; adapters carry no secrets). */
export const CENSUS_API_BASE = "https://api.census.gov/data";

const POPULATION: Seed[] = [
  ["B01003_001E", "Total population", "people"],
  ["B01001_002E", "Male population", "people"],
  ["B01001_026E", "Female population", "people"],
  ["B02001_002E", "White alone", "people"],
  ["B02001_003E", "Black or African American alone", "people"],
  ["B02001_004E", "American Indian and Alaska Native alone", "people"],
  ["B02001_005E", "Asian alone", "people"],
  ["B02001_006E", "Native Hawaiian and Other Pacific Islander alone", "people"],
  ["B02001_007E", "Some other race alone", "people"],
  ["B02001_008E", "Two or more races", "people"],
  ["B03003_003E", "Hispanic or Latino (of any race)", "people"],
];

const AGE: Seed[] = [
  ["B01002_001E", "Median age", "years"],
  ["B01002_002E", "Median age — male", "years"],
  ["B01002_003E", "Median age — female", "years"],
  ["B09001_001E", "Population under 18", "people"],
  ["B01001_020E", "Male 65 and 66 years", "people"],
  ["B01001_044E", "Female 65 and 66 years", "people"],
];

const INCOME: Seed[] = [
  ["B19013_001E", "Median household income", "USD"],
  ["B19301_001E", "Per capita income", "USD"],
  ["B19025_001E", "Aggregate household income", "USD"],
  ["B17001_002E", "Income below poverty level (past 12 months)", "people"],
  ["B19001_002E", "Households with income < $10,000", "households"],
  ["B19001_017E", "Households with income $200,000 or more", "households"],
  ["B19113_001E", "Median family income", "USD"],
  ["B19202_001E", "Median nonfamily household income", "USD"],
];

const HOUSING: Seed[] = [
  ["B25001_001E", "Total housing units", "housing units"],
  ["B25002_002E", "Occupied housing units", "housing units"],
  ["B25002_003E", "Vacant housing units", "housing units"],
  ["B25003_002E", "Owner-occupied housing units", "housing units"],
  ["B25003_003E", "Renter-occupied housing units", "housing units"],
  ["B25077_001E", "Median value (owner-occupied units)", "USD"],
  ["B25064_001E", "Median gross rent", "USD"],
  ["B25035_001E", "Median year structure built", "years"],
  ["B25024_002E", "1-unit detached structures", "housing units"],
  ["B25024_007E", "Structures with 10 to 19 units", "housing units"],
];

const EDUCATION: Seed[] = [
  ["B15003_001E", "Population 25 years and over", "people"],
  ["B15003_017E", "Regular high school diploma", "people"],
  ["B15003_021E", "Associate's degree", "people"],
  ["B15003_022E", "Bachelor's degree", "people"],
  ["B15003_023E", "Master's degree", "people"],
  ["B15003_024E", "Professional school degree", "people"],
  ["B15003_025E", "Doctorate degree", "people"],
];

const EMPLOYMENT: Seed[] = [
  ["B23025_002E", "In labor force", "people"],
  ["B23025_004E", "Civilian employed", "people"],
  ["B23025_005E", "Unemployed", "people"],
  ["B23025_007E", "Not in labor force", "people"],
  ["C24010_001E", "Employed population 16 and over (by occupation)", "people"],
];

const HOUSEHOLDS: Seed[] = [
  ["B11001_001E", "Total households", "households"],
  ["B11001_002E", "Family households", "households"],
  ["B11001_007E", "Nonfamily households", "households"],
  ["B25010_001E", "Average household size", "people"],
  ["B11003_001E", "Families", "households"],
];

const COMMUTE: Seed[] = [
  ["B08301_001E", "Workers 16 and over (commute)", "people"],
  ["B08301_003E", "Drove alone to work", "people"],
  ["B08301_010E", "Public transportation to work", "people"],
  ["B08303_001E", "Aggregate travel time to work", "minutes"],
  ["B08013_001E", "Aggregate travel time (all workers)", "minutes"],
];

const CONNECTIVITY: Seed[] = [
  ["B28002_001E", "Total households (internet subscription)", "households"],
  ["B28002_004E", "Households with a broadband subscription", "households"],
  ["B28002_013E", "Households with no internet access", "households"],
];

const ALL_SEEDS: Seed[] = [
  ...POPULATION,
  ...AGE,
  ...INCOME,
  ...HOUSING,
  ...EDUCATION,
  ...EMPLOYMENT,
  ...HOUSEHOLDS,
  ...COMMUTE,
  ...CONNECTIVITY,
];

/** The static US Census variable catalog (55+ entries) — no network. */
export const US_CENSUS_CATALOG: DemographicVariable[] = buildCatalog(ALL_SEEDS, {
  country: "US",
  citation: US_CENSUS_CITATION,
  tiers: US_CENSUS_TIERS,
});

/** Census Data API `for=` clause per tier (deploy-time query detail). */
const TIER_FOR: Record<string, string> = {
  state: "state:*",
  county: "county:*",
  zcta: "zip code tabulation area:*",
  tract: "tract:*",
};

/** Census Data API response: header row + string rows. */
type CensusTable = string[][];

export function createUsCensusAdapter(): OverlayAdapter {
  return {
    source: US_CENSUS_SOURCE,
    country: "US",
    tiers: US_CENSUS_TIERS,

    catalog() {
      return US_CENSUS_CATALOG;
    },

    async fetchRawBoundaries(http: HttpClient, tier: Tier, vintage) {
      // TIGER/Line served as GeoJSON (deploy-time: a tiger→geojson artifact URL).
      return http.getJson<FeatureCollection>(`${CENSUS_API_BASE}/${vintage}/tiger/${tier}.geojson`);
    },

    normalizeBoundaries(raw, tier, vintage): Boundary[] {
      return normalizeFeatureCollection(raw, {
        country: "US",
        tier,
        vintage,
        idFrom: (props) => String(props.GEOID ?? props.geoid ?? props.AFFGEOID ?? ""),
        nameFrom: (props) => props.NAME ?? props.name,
      });
    },

    async fetchRawValues(http: HttpClient, variableId: string, tier: Tier, vintage) {
      return http.getJson<CensusTable>(`${CENSUS_API_BASE}/${vintage}/acs/acs5`, {
        params: { get: `NAME,${variableId}`, for: TIER_FOR[tier] ?? "state:*" },
      });
    },

    normalizeValues(raw, variableId, _tier, vintage): DemographicDatum[] {
      const table = raw as CensusTable;
      if (!Array.isArray(table) || table.length < 2) return [];
      const header = table[0]!;
      const valueIdx = header.indexOf(variableId);
      if (valueIdx === -1) return [];
      // Geography columns are every non-data column (NAME + the `for`/`in` ids);
      // the boundary id is the concatenation of the id columns == the TIGER GEOID.
      const nameIdx = header.indexOf("NAME");
      const idIdxs = header
        .map((_, i) => i)
        .filter((i) => i !== valueIdx && i !== nameIdx);
      const out: DemographicDatum[] = [];
      for (let r = 1; r < table.length; r++) {
        const row = table[r]!;
        const id = idIdxs.map((i) => row[i] ?? "").join("");
        if (id === "") continue;
        const rawVal = row[valueIdx];
        const num = rawVal == null || rawVal === "" ? null : Number(rawVal);
        out.push({
          boundaryId: id,
          variableId,
          vintage,
          value: num !== null && Number.isFinite(num) ? num : null,
        });
      }
      return out;
    },
  };
}
